// TenantModal.jsx
import React from "react";
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
  Typography,
  Tabs,
  Tab,
  Divider,
} from "@mui/material";

import TenantFormFields from "./TenantFormFields";
import TenantContactsSection from "./sections/TenantContactsSection";
import TenantImagesSection from "./sections/TenantImagesSection";
import TenantAdminCredentialsSection from "./sections/TenantAdminCredentialsSection";
import TenantAccessSection from "./sections/TenantAccessSection";
import TenantUsageSection from "./sections/TenantUsageSection";
import TenantDangerZoneSection from "./sections/TenantDangerZoneSection";
import TenantUsersSection from "./sections/TenantUsersSection/TenantUsersSection";

import useTenantModalController from "./useTenantModalController";

function TabPanel({ value, index, children }) {
  if (value !== index) return null;
  return <Box sx={{ pt: 1 }}>{children}</Box>;
}

export default function TenantModal({ open, tenantId, onClose, onChanged }) {
  const c = useTenantModalController({ open, tenantId, onClose, onChanged });

  // NOTE: indices must match c.tabs order (see useTenantModalController)
  // Create mode tabs: [0 general, 1 contacts, 2 admin]
  // Edit mode tabs:   [0 general, 1 contacts, 2 branding, 3 access, 4 users, 5 usage, 6 danger]
  const usersTabIndex = c.isEdit ? 4 : null;
  const usageTabIndex = c.isEdit ? 5 : null;
  const dangerTabIndex = c.isEdit ? 6 : null;

  return (
    <Dialog
      open={open}
      onClose={c.saving ? undefined : onClose}
      fullWidth
      maxWidth={false}
      PaperProps={{
        sx: {
          width: "95vw",
          height: "92vh",
          maxWidth: "1400px",
        },
      }}
    >
      <DialogTitle>
        {c.isEdit
          ? c.t("tenantModal.title.detail", "Detail organizácie")
          : c.t("tenantModal.title.create", "Vytvoriť organizáciu")}
      </DialogTitle>

      <DialogContent dividers sx={{ p: 0, height: "100%", overflow: "hidden" }}>
        {c.loading ? (
          <Box sx={{ py: 6, display: "flex", justifyContent: "center" }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ display: "flex", height: "100%" }}>
            {/* Left nav */}
            <Box sx={{ width: 240, borderRight: 1, borderColor: "divider" }}>
              <Tabs
                orientation="vertical"
                value={c.tab}
                onChange={(_, v) => c.setTab(v)}
                variant="scrollable"
              >
                {c.tabs.map((x) => (
                  <Tab key={x.key} label={x.label} sx={{ alignItems: "flex-start" }} />
                ))}
              </Tabs>
            </Box>

            {/* Right content */}
            <Box sx={{ flex: 1, p: 2, overflow: "auto" }}>
              {/* 0: General */}
              <TabPanel value={c.tab} index={0}>
                <TenantFormFields
                  form={c.form}
                  setForm={c.setForm}
                  disabled={c.saving}
                  isEdit={c.isEdit}
                />
              </TabPanel>

              {/* 1: Contacts */}
              <TabPanel value={c.tab} index={1}>
                <TenantContactsSection form={c.form} setForm={c.setForm} disabled={c.saving} />
              </TabPanel>

              {/* 2: Create only - Admin credentials */}
              {!c.isEdit ? (
                <TabPanel value={c.tab} index={2}>
                  <TenantAdminCredentialsSection
                    adminUsername={c.adminUsername}
                    form={c.form}
                    setForm={c.setForm}
                    saving={c.saving}
                  />
                </TabPanel>
              ) : null}

              {/* 2: Edit only - Branding */}
              {c.isEdit ? (
                <TabPanel value={c.tab} index={2}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    {c.t("tenantModal.tenantIdLabel", "ID organizácie")}: {tenantId}
                  </Typography>

                  <TenantImagesSection
                    tenantId={tenantId}
                    preview={c.preview}
                    saving={c.saving}
                    onPick={c.onPick}
                    onRemoveLogo={() => c.removeImg("logo")}
                    onRemoveBg={() => c.removeImg("bg")}
                  />
                </TabPanel>
              ) : null}

              {/* 3: Edit only - Access */}
              {c.isEdit ? (
                <TabPanel value={c.tab} index={3}>
                  <TenantAccessSection
                    tenant={c.form}
                    setForm={c.setForm} // <-- Add this prop
                    disabled={c.saving}
                    onActivate={c.handleActivate}
                    onDeactivate={c.handleDeactivate}
                    onSetTrialExpiration={c.handleSetTrialExpiration}
                  />
                </TabPanel>
              ) : null}

              {/* 4: Edit only - Users (NEW) */}
              {c.isEdit ? (
                <TabPanel value={c.tab} index={usersTabIndex}>
                  <TenantUsersSection tenantId={tenantId} disabled={c.saving} />
                </TabPanel>
              ) : null}

              {/* 5: Edit only - Usage */}
              {c.isEdit ? (
                <TabPanel value={c.tab} index={usageTabIndex}>
                  <TenantUsageSection
                    disabled={c.saving}
                    onLoadStats={c.loadStats}
                    onLoadLogs={c.loadLogs}
                    onLoadAnonymizationStats={c.loadAnonymizationStats}
                  />
                </TabPanel>
              ) : null}

              {/* 6: Edit only - Danger */}
              {c.isEdit ? (
                <TabPanel value={c.tab} index={dangerTabIndex}>
                  <Divider sx={{ mb: 2 }} />
                  <TenantDangerZoneSection
                    tenantName={c.form.name}
                    disabled={c.saving}
                    confirmOpen={c.confirmDelete}
                    onOpenConfirm={() => c.setConfirmDelete(true)}
                    onCloseConfirm={() => c.setConfirmDelete(false)}
                    onDelete={c.doDelete}
                  />
                </TabPanel>
              ) : null}
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ justifyContent: "space-between" }}>
        <Box />
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button onClick={onClose} disabled={c.saving}>
            {c.t("tenantModal.actions.cancel", "Zrušiť")}
          </Button>
          <Button variant="contained" onClick={c.saveAll} disabled={c.saving || c.loading}>
            {c.saving
              ? c.t("tenantModal.actions.saving", "Ukladám…")
              : c.isEdit
                ? c.t("tenantModal.actions.save", "Uložiť")
                : c.t("tenantModal.actions.create", "Vytvoriť")}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
}